import React, { useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import FormField from "./FormField";
import { ComponentErrorBoundary } from "../ErrorBoundary";
import {
  ErrorBadge,
  ProgressBadge,
  SectionHeaderBadge,
  CategoryIcon,
  StatusBadge,
} from "../ui/StatusIndicators";

/**
 * Form Section Component
 *
 * Renders a section of the form with optional collapsible functionality.
 * Handles field layout and section-specific styling.
 */
export default function FormSection({
  section,
  fields,
  formData,
  validationErrors,
  disabled = false,
  onFieldChange,
  onFieldValidation,
  className = "",
  ...props
}) {
  const [isCollapsed, setIsCollapsed] = useState(true); // Default to collapsed

  // Determine section grid layout
  const getGridClasses = () => {
    const columns = section.columns || section.grid?.columns || 1;
    switch (columns) {
      case 2:
        return "md:grid-cols-2";
      case 3:
        return "md:grid-cols-3";
      case 4:
        return "md:grid-cols-4";
      default:
        return "grid-cols-1";
    }
  };

  // Count valid fields in this section
  const validFields =
    section.fields?.filter((fieldId) => fields[fieldId]) || [];

  // Count fields with errors in this section
  const sectionErrors = validFields.filter(
    (fieldId) =>
      validationErrors[fieldId] && validationErrors[fieldId].length > 0
  ).length;

  // Calculate completion metrics
  const totalFields = validFields.length;
  const completedFields = validFields.filter((fieldId) => {
    const value = formData[fields[fieldId]?.name || fieldId];
    return value !== null && value !== undefined && value !== "";
  }).length;

  const requiredFields = validFields.filter(
    (fieldId) => fields[fieldId]?.required
  ).length;

  const completedRequiredFields = validFields.filter((fieldId) => {
    const field = fields[fieldId];
    if (!field?.required) return false;
    const value = formData[field.name || fieldId];
    return value !== null && value !== undefined && value !== "";
  }).length;

  // Determine section status
  const getSectionStatus = () => {
    if (sectionErrors > 0) return "invalid";
    if (requiredFields > 0 && completedRequiredFields < requiredFields)
      return "incomplete";
    if (totalFields > 0 && completedFields === totalFields) return "complete";
    return "incomplete";
  };

  // Get category for section icon
  const getSectionCategory = () => {
    const title = section.title?.toLowerCase() || "";
    if (
      title.includes("financial") ||
      title.includes("budget") ||
      title.includes("cost")
    )
      return "financial";
    if (
      title.includes("location") ||
      title.includes("address") ||
      title.includes("property")
    )
      return "location";
    if (
      title.includes("timeline") ||
      title.includes("date") ||
      title.includes("schedule")
    )
      return "timeline";
    if (
      title.includes("contact") ||
      title.includes("team") ||
      title.includes("people")
    )
      return "contacts";
    if (
      title.includes("basic") ||
      title.includes("information") ||
      title.includes("details")
    )
      return "default";
    return "default";
  };

  // Handle section toggle - all sections are collapsible by default
  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (!section.fields || section.fields.length === 0) {
    return null;
  }

  return (
    <ComponentErrorBoundary componentName="FormSection">
      <div
        className={`form-section bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}
        {...props}
      >
        {/* Section Header - Enhanced with Visual Cues */}
        <div
          className={`section-header px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${
            isCollapsed ? "" : "border-b border-gray-200 bg-gray-25"
          }`}
          onClick={handleToggle}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={
                    isCollapsed ? "Expand section" : "Collapse section"
                  }
                >
                  {isCollapsed ? (
                    <ChevronRightIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>

                {/* Section Category Icon */}
                <CategoryIcon
                  category={getSectionCategory()}
                  size="md"
                  className="flex-shrink-0"
                />

                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3
                      className={`text-lg font-medium ${
                        sectionErrors > 0 ? "text-red-700" : "text-gray-900"
                      }`}
                    >
                      {section.title}
                    </h3>

                    {/* Section Status Badge */}
                    <StatusBadge
                      status={getSectionStatus()}
                      size="xs"
                      variant="default"
                      className="ml-2"
                    />
                  </div>

                  {section.description && !isCollapsed && (
                    <p className="text-sm text-gray-600 mt-1">
                      {section.description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Section Indicators */}
            <div className="flex items-center space-x-2">
              {/* Progress Badge */}
              {totalFields > 0 && (
                <ProgressBadge
                  completed={completedFields}
                  total={totalFields}
                  size="sm"
                  showIcon={false}
                />
              )}

              {/* Error Badge */}
              <ErrorBadge errorCount={sectionErrors} size="sm" />

              {/* Field Count Badge */}
              <SectionHeaderBadge
                type="fields"
                count={validFields.length}
                size="sm"
              />

              {/* Required Fields Indicator */}
              {requiredFields > 0 && (
                <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded-full border">
                  {completedRequiredFields}/{requiredFields} required
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Section Content */}
        {!isCollapsed && (
          <div className="section-content px-6 py-5 animate-fadeIn">
            <div className={`grid gap-6 ${getGridClasses()}`}>
              {section.fields.map((fieldId, index) => {
                const field = fields[fieldId];

                if (!field) {
                  // Log missing field for debugging
                  console.warn(`Field definition not found: ${fieldId}`);
                  return (
                    <div
                      key={fieldId}
                      className="text-sm text-red-600 p-2 bg-red-50 border border-red-200 rounded"
                    >
                      Missing field definition: {fieldId}
                    </div>
                  );
                }

                // Check conditional logic
                if (!shouldShowField(field, formData)) {
                  return null;
                }

                return (
                  <FormField
                    key={fieldId}
                    field={field}
                    value={formData[field.name || fieldId]}
                    errors={validationErrors[fieldId]}
                    disabled={disabled}
                    onChange={(value) =>
                      onFieldChange(field.name || fieldId, value)
                    }
                    onValidation={(errors) =>
                      onFieldValidation(fieldId, errors)
                    }
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ComponentErrorBoundary>
  );
}

/**
 * Check if a field should be shown based on conditional logic
 */
function shouldShowField(field, formData) {
  if (!field.conditional) {
    return true;
  }

  const { enable_when } = field.conditional;
  if (!enable_when) {
    return true;
  }

  const { field: dependentField, operator, value: expectedValue } = enable_when;
  const actualValue = formData[dependentField];

  switch (operator) {
    case "equals":
      return actualValue === expectedValue;

    case "not_equals":
      return actualValue !== expectedValue;

    case "not_empty":
      return (
        actualValue !== null && actualValue !== undefined && actualValue !== ""
      );

    case "empty":
      return (
        actualValue === null || actualValue === undefined || actualValue === ""
      );

    case "contains":
      return (
        typeof actualValue === "string" && actualValue.includes(expectedValue)
      );

    case "greater_than":
      return Number(actualValue) > Number(expectedValue);

    case "less_than":
      return Number(actualValue) < Number(expectedValue);

    case "in":
      return (
        Array.isArray(expectedValue) && expectedValue.includes(actualValue)
      );

    case "not_in":
      return (
        Array.isArray(expectedValue) && !expectedValue.includes(actualValue)
      );

    default:
      console.warn(`Unknown conditional operator: ${operator}`);
      return true;
  }
}
